import { useState, useEffect, useCallback } from "react";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "sonner";
import { GraduationCap, Loader2, LogOut, Mail, CalendarX, CheckCircle2, FileCheck2, Pencil, Clock } from "lucide-react";
import api, { formatError } from "@/lib/api";
import applicantApi, {
  getApplicantToken, setApplicantToken, clearApplicantToken, APPLICANT_TOKEN_KEY, APPLICANT_SESSION_EXPIRED,
} from "@/lib/applicantApi";
import { watchExpiry } from "@/lib/session";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { valueLabel, fmtDateTime } from "@/i18n";
import ApplicationForm from "@/pages/ApplicationForm";
import MainSiteLink from "@/components/MainSiteLink";

const CONTACT = "contact@penda.org.mk";
const MailLink = ({ children }) => <a href={`mailto:${CONTACT}`} className="text-primary underline">{children}</a>;

function Shell({ email, onLogout, children }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-primary-foreground" strokeWidth={1.5} />
            </div>
            <span className="font-heading text-lg font-semibold tracking-tight">{t("portal.brand")}</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <MainSiteLink />
            {email && (
              <>
                <span className="text-muted-foreground hidden md:inline">{email}</span>
                <Button variant="ghost" size="sm" onClick={onLogout} data-testid="applicant-logout-btn">
                  <LogOut className="w-4 h-4 mr-1.5" /> {t("portal.log_out")}
                </Button>
              </>
            )}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

const Card = ({ children, className = "", ...rest }) => (
  <div className={`bg-card border border-border rounded-2xl p-6 md:p-10 shadow-sm animate-fade-in ${className}`} {...rest}>{children}</div>
);

function ClosedNotice({ deadline }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-3 bg-muted/60 border border-border rounded-xl p-4 mb-6 text-sm" data-testid="applications-closed">
      <CalendarX className="w-5 h-5 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
      <p className="text-muted-foreground leading-relaxed">
        {deadline ? t("portal.closed_deadline", { deadline }) : t("portal.closed")}
      </p>
    </div>
  );
}

function Login({ status, onLoggedIn }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const requestCode = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/applicant/request-code", { email: email.trim() });
      setSentTo(email.trim().toLowerCase());
      setCode("");
      setResendIn(60);
      toast.success(t("portal.code_sent"));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("portal.could_not_send_code"));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/applicant/verify-code", { email: sentTo, code: code.trim() });
      setApplicantToken(res.data.token);
      onLoggedIn();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("errors.login_failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {status && !status.open && <ClosedNotice deadline={status.deadline_display} />}
      <Card className="max-w-lg mx-auto">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-5">
          <Mail className="w-6 h-6 text-primary" strokeWidth={1.5} />
        </div>
        {!sentTo ? (
          <form onSubmit={requestCode}>
            <h1 className="font-heading text-2xl font-semibold tracking-tight mb-2">{t("portal.login_title")}</h1>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">{t("portal.login_intro")}</p>
            <Label className="text-sm mb-2 block">{t("common.email")}</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email"
              required className="h-12 mb-5" data-testid="login-email" />
            <Button type="submit" className="w-full h-11" disabled={busy} data-testid="request-code-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("portal.send_code")}
            </Button>
          </form>
        ) : (
          <form onSubmit={verify}>
            <h1 className="font-heading text-2xl font-semibold tracking-tight mb-2">{t("portal.check_title")}</h1>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              <Trans i18nKey="portal.check_text" values={{ email: sentTo }} components={{ b: <b className="text-foreground" /> }} />
            </p>
            <Label className="text-sm mb-2 block">{t("portal.code")}</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric" autoComplete="one-time-code" placeholder="123456" autoFocus
              className="h-12 mb-5 text-lg tracking-[0.4em] font-mono" data-testid="login-code" />
            <Button type="submit" className="w-full h-11" disabled={busy || code.length !== 6} data-testid="verify-code-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("portal.log_in")}
            </Button>
            <div className="flex items-center justify-between mt-5 text-sm">
              <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setSentTo(null)}>
                {t("portal.different_email")}
              </button>
              <button type="button" className="text-primary disabled:text-muted-foreground" disabled={resendIn > 0 || busy}
                onClick={requestCode}>
                {resendIn > 0 ? t("portal.resend_in", { seconds: resendIn }) : t("portal.resend")}
              </button>
            </div>
          </form>
        )}
      </Card>
    </>
  );
}

function Overview({ me, justSubmitted, onEdit }) {
  const { t } = useTranslation();
  const a = me.application;
  const docLabel = (key) => t(`documents.${key}.label`, { defaultValue: key });
  const rows = [
    ["name", `${a.name} ${a.surname}`], ["university", valueLabel(a.university)],
    ["field_of_study", valueLabel(a.field_of_study)], ["study_program", valueLabel(a.study_program)], ["gpa", a.gpa],
  ].filter(([, v]) => v && String(v).trim());

  const openFile = async (f) => {
    try {
      const res = await applicantApi.get(`/applicant/files/${f.storage_path}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank", "noopener");
    } catch {
      toast.error(t("errors.could_not_open_document"));
    }
  };

  return (
    <>
      {justSubmitted && (
        <div className="flex items-start gap-3 bg-primary/5 border border-primary/20 rounded-xl p-4 mb-6" data-testid="success-message">
          <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <p className="text-sm leading-relaxed"><Trans i18nKey="portal.submitted_notice" components={{ b: <b /> }} /></p>
        </div>
      )}
      {!me.open && <ClosedNotice deadline={me.deadline_display} />}
      <Card data-testid="my-application">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <p className="text-sm text-muted-foreground uppercase tracking-[0.2em] mb-1">{t("portal.my_application")}</p>
            <h1 className="font-heading text-2xl font-semibold tracking-tight">{a.name} {a.surname}</h1>
          </div>
          {a.status === "submitted" ? (
            <span className="inline-flex items-center gap-1.5 text-sm bg-primary/10 text-primary rounded-full px-3 py-1" data-testid="application-status">
              <CheckCircle2 className="w-4 h-4" /> {t("portal.status_submitted")}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-sm bg-muted text-foreground rounded-full px-3 py-1" data-testid="application-status">
              <Clock className="w-4 h-4" /> {t("portal.status_under_review")}
            </span>
          )}
        </div>

        <p className="text-sm text-muted-foreground mb-6">
          {t("portal.submitted_on", { date: fmtDateTime(a.submitted_at) })}
          {a.updated_at !== a.submitted_at && t("portal.last_changed", { date: fmtDateTime(a.updated_at) })}
        </p>

        <dl className="divide-y divide-border border-y border-border mb-6">
          {rows.map(([k, v]) => (
            <div key={k} className="grid grid-cols-3 gap-4 py-3 text-sm">
              <dt className="text-muted-foreground">{t(`fields.${k}`)}</dt>
              <dd className="col-span-2">{v}</dd>
            </div>
          ))}
        </dl>

        <h2 className="font-heading text-lg font-medium mb-3">{t("portal.documents", { count: a.files.length + me.references.length })}</h2>
        <div className="grid sm:grid-cols-2 gap-2 mb-8">
          {me.references.map((r) => (
            <div key={r.slot} className="flex items-center gap-3 border border-border rounded-lg px-3 py-2" data-testid={`overview-${r.slot}`}>
              {r.status === "received"
                ? <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                : <Clock className="w-4 h-4 text-muted-foreground shrink-0" />}
              <span className="min-w-0">
                <span className="block text-xs text-muted-foreground">{docLabel(r.slot)}</span>
                <span className="block text-sm truncate">
                  {t(r.status === "received" ? "portal.received_from" : "portal.requested_from", { name: r.referee_name })}
                </span>
              </span>
            </div>
          ))}
          {a.files.map((f) => (
            <button key={f.storage_path} onClick={() => openFile(f)}
              className="flex items-center gap-3 border border-border rounded-lg px-3 py-2 text-left hover:bg-muted/50 transition-colors">
              <FileCheck2 className="w-4 h-4 text-primary shrink-0" />
              <span className="min-w-0">
                <span className="block text-xs text-muted-foreground">{docLabel(f.field)}</span>
                <span className="block text-sm truncate">{f.original_filename}</span>
              </span>
            </button>
          ))}
        </div>

        {me.can_edit ? (
          <div className="flex items-center justify-between gap-4 flex-wrap border-t border-border pt-6">
            <p className="text-sm text-muted-foreground">
              {me.deadline_display ? t("portal.can_edit_until", { deadline: me.deadline_display }) : t("portal.can_edit")}
            </p>
            <Button onClick={onEdit} data-testid="edit-application-btn"><Pencil className="w-4 h-4 mr-2" /> {t("portal.edit")}</Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground border-t border-border pt-6">{t("portal.locked")}</p>
        )}
      </Card>
    </>
  );
}

export default function ApplicantPortal() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);     // public submission status, for the login screen
  const [me, setMe] = useState(null);             // null = not loaded / logged out
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);

  const loadMe = useCallback(async () => {
    if (!getApplicantToken()) { setMe(null); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await applicantApi.get("/applicant/me");
      setMe(res.data);
    } catch (err) {
      if (err.response?.status === 401) clearApplicantToken();
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/submission-status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
    loadMe();
  }, [loadMe]);

  // session timed out (see lib/session.js): back to the login form; the draft is saved on the server
  useEffect(() => {
    const expired = () => { setMe(null); setEditing(false); setJustSubmitted(false); };
    window.addEventListener(APPLICANT_SESSION_EXPIRED, expired);
    const stopWatching = watchExpiry(APPLICANT_TOKEN_KEY, expired);
    return () => { window.removeEventListener(APPLICANT_SESSION_EXPIRED, expired); stopWatching(); };
  }, []);

  const logout = () => {
    clearApplicantToken();
    setMe(null);
    setEditing(false);
    setJustSubmitted(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!me) {
    return <Shell><Login status={status} onLoggedIn={loadMe} /></Shell>;
  }

  const afterSave = async (submittedNow) => {
    setEditing(false);
    setJustSubmitted(submittedNow);
    if (!submittedNow) toast.success(t("portal.changes_saved"));
    window.scrollTo(0, 0);
    await loadMe();
  };

  let body;
  if (me.application && editing && me.can_edit) {
    body = <ApplicationForm key="edit" mode="edit" email={me.email} initial={me.application} references={me.references}
      onDone={() => afterSave(false)} onCancel={() => { setEditing(false); loadMe(); }} />;
  } else if (me.application) {
    body = <Overview me={me} justSubmitted={justSubmitted} onEdit={() => setEditing(true)} />;
  } else if (me.open) {
    body = <ApplicationForm key="draft" mode="draft" email={me.email} initial={me.draft} references={me.references}
      onDone={() => afterSave(true)} />;
  } else {
    body = (
      <>
        <ClosedNotice deadline={me.deadline_display} />
        <Card className="text-center">
          <p className="text-muted-foreground text-sm">
            <Trans i18nKey="portal.no_application" values={{ email: me.email, contact: CONTACT }}
              components={{ b: <b className="text-foreground" />, mail: <MailLink /> }} />
          </p>
        </Card>
      </>
    );
  }

  return <Shell email={me.email} onLogout={logout}>{body}</Shell>;
}
